import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface FairDealApplicationInsights {
  processingWeeks: string;
  approvalRate: string;
  avgWeeklyContribution: string;
  homesOnScheme: string;
  propertyCapYears: string;
  keyUpdate2026: string;
  documentsNeeded: string[];
  commonMistakes: string[];
}

const CACHE_KEY = "grandpal_fair_deal_application_v1";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

function isValid(d: unknown): d is FairDealApplicationInsights {
  if (!d || typeof d !== "object") return false;
  const obj = d as Record<string, unknown>;
  return (
    typeof obj.processingWeeks === "string" &&
    typeof obj.approvalRate === "string" &&
    Array.isArray(obj.documentsNeeded)
  );
}

export function useFairDealApplicationInsights() {
  const [insights, setInsights] = useState<FairDealApplicationInsights | null>(null);
  const [citations, setCitations] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_TTL_MS && isValid(data.insights)) {
          setInsights(data.insights);
          setCitations(data.citations || []);
          setIsLoading(false);
          return;
        }
        localStorage.removeItem(CACHE_KEY);
      }
    } catch (_) {
      localStorage.removeItem(CACHE_KEY);
    }

    const fetchData = async () => {
      try {
        const { data, error: fnErr } = await supabase.functions.invoke(
          "fair-deal-application-insights"
        );
        if (fnErr) throw new Error(fnErr.message);
        if (isValid(data.insights)) {
          setInsights(data.insights);
          setCitations(data.citations || []);
          try {
            localStorage.setItem(
              CACHE_KEY,
              JSON.stringify({ data, timestamp: Date.now() })
            );
          } catch (_) {}
        } else {
          throw new Error("Invalid data");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  return { insights, citations, isLoading, error };
}
